import { LockOutlined as LockIcon, Search as SearchIcon } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    InputAdornment,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useFormatter } from '../../context/FormatterContext';
import StyledPagination from '../common/StyledPagination';
import { useUserPlanStatus } from '../../hooks/useUserPlanStatus';
import { InsightData, ShortLongData, ShortLongItem } from '../../services/insights.types';
import { AddonApiKey } from '../../services/payment.types';
import { getDisplayCoin } from '../../utils/displayFormatters';
import FilledTableSortLabel from '../common/FilledTableSortLabel';
import PageLayout from '../common/PageLayout';
import { useInsightsService } from '../../services/insights.service';

type Order = 'asc' | 'desc';
type OrderBy = keyof InsightData | 'asset' | 'live_amount' | 'amount' | 'column1' | 'column2' | 'column3' | 'column4' | 'column5' | 'column6' | 'column7';

interface HeadCell {
    id: OrderBy;
    label: string | JSX.Element;
    numeric: boolean;
}

const createHeadCells = (t: (key: string) => string): readonly HeadCell[] => {
    const today = new Date();
    const { formatDateTwoLines } = useFormatter();

    const getDateWithMonthsOffset = (months: number): Date => {
        const date = new Date(today);
        date.setMonth(today.getMonth() + months);
        return date;
    };

    const createColumnLabel = (date: Date, isToday: boolean = false, months: number = 0): JSX.Element => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="body2">{formatDateTwoLines(Math.floor(date.getTime() / 1000)).date}</Typography>
                <Typography variant="caption">
                    {isToday ? t('shortlong.today') : t('shortlong.monthsLater', { months: months.toString() })}
                </Typography>
            </Box>
        );
    };

    return [
        { id: 'asset', label: t('shortlong.columns.asset'), numeric: false },
        { id: 'live_amount', label: t('shortlong.columns.liveAmount'), numeric: true },
        { id: 'amount', label: t('shortlong.columns.amount'), numeric: true },
        { id: 'column1', label: createColumnLabel(today, true), numeric: true },
        { id: 'column2', label: createColumnLabel(getDateWithMonthsOffset(2), false, 2), numeric: true },
        { id: 'column3', label: createColumnLabel(getDateWithMonthsOffset(4), false, 4), numeric: true },
        { id: 'column4', label: createColumnLabel(getDateWithMonthsOffset(6), false, 6), numeric: true },
        { id: 'column5', label: createColumnLabel(getDateWithMonthsOffset(8), false, 8), numeric: true },
        { id: 'column6', label: createColumnLabel(getDateWithMonthsOffset(10), false, 10), numeric: true },
        { id: 'column7', label: createColumnLabel(getDateWithMonthsOffset(12), false, 12), numeric: true },
    ];
};

const ShortLongPage: React.FC = () => {
    const { t } = useTranslation();
    const headCells = useMemo(() => createHeadCells(t), [t]);
    const { formatCrypto } = useFormatter();
    const insightsService = useInsightsService();
    const theme = useTheme();
    const { hasAddon, isLoadingUserPlans } = useUserPlanStatus();

    // Check if user has Portfolio Insights addon
    const hasPortfolioInsightsAddon = hasAddon(AddonApiKey.ADVANCED_ANALYTICS);

    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('amount');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const isAllowed = hasPortfolioInsightsAddon && !isLoadingUserPlans;

    // Get gains data for coin and amount information
    const { data: gainsData, isLoading: isGainsLoading } = useQuery({
        queryKey: ['insights-gains'],
        queryFn: () => insightsService.getGainsData(),
        enabled: isAllowed,
    });

    // Get short-long data for other columns
    const { data: shortLongData, isLoading: isShortLongLoading } = useQuery<ShortLongData>({
        queryKey: ['short-long-positions'],
        queryFn: () => insightsService.getShortLongPositions(null, null, null),
        enabled: isAllowed && !!gainsData,
    });
    
    // Get live price data
    const { data: livePrice, isLoading: isLivePriceLoading } = useQuery({
        queryKey: ['insight-live-price', gainsData],
        queryFn: async () => insightsService.getCoinBalanceForAccount(),
        enabled: isAllowed && !!gainsData,
    });

    // Combine loading states
    const isLoading = isGainsLoading || isShortLongLoading || isLivePriceLoading;

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };



    // Define interface for processed short-long data
    interface ProcessedShortLongItem {
        coin: string;
        columns: (ShortLongItem | undefined)[];
    }

    // Process the short-long data to extract the long positions
    const processedShortLongData = useMemo<ProcessedShortLongItem[]>(() => {
        if (!shortLongData || !shortLongData.data || !shortLongData.data.long) {
            return [];
        }
        
        const longData = shortLongData.data.long;
        
        if (!Array.isArray(longData) || longData.length === 0) {
            return [];
        }
        
        // Create a map of all unique coins from all columns
        const uniqueCoins = new Map<string, ProcessedShortLongItem>();
        
        // Process all columns to find all unique coins
        for (let colIndex = 0; colIndex < longData.length; colIndex++) {
            if (Array.isArray(longData[colIndex])) {
                longData[colIndex].forEach(item => {
                    if (!uniqueCoins.has(item.coin)) {
                        // Initialize a new coin entry with empty columns array
                        uniqueCoins.set(item.coin, {
                            coin: item.coin,
                            columns: new Array(longData.length)
                        });
                    }
                    
                    // Add this column's data to the coin's columns array
                    const coinData = uniqueCoins.get(item.coin);
                    if (coinData) {
                        coinData.columns[colIndex] = item;
                    }
                });
            }
        }
        
        // Convert map to array
        return Array.from(uniqueCoins.values());
    }, [shortLongData]);

    // Combine the data from both APIs
    const combinedData = useMemo(() => {
        if (!gainsData || !processedShortLongData.length) return [];
        
        // Create a map of currencies from processed short-long data
        const shortLongMap = new Map();
        processedShortLongData.forEach((item: ProcessedShortLongItem) => {
            shortLongMap.set(item.coin, item);
        });
        
        // Filter gains data to only include coins that are in the short-long data
        const filteredData = gainsData.filter(gainsItem => {
            return shortLongMap.has(gainsItem.currency);
        }).map(gainsItem => {
            const shortLongItem = shortLongMap.get(gainsItem.currency);
            // Add live amount from live price data
            const liveAmount = livePrice?.find(price => price.coin === gainsItem.currency)?.quantity;
            
            return {
                ...gainsItem,
                live_amount: liveAmount,
                // Add the processed short-long data
                shortLongData: shortLongItem || null
            };
        });
        
        return filteredData;
    }, [gainsData, processedShortLongData, livePrice]);

    const filteredData = useMemo(() => {
        if (!combinedData) return [];
        return combinedData.filter((item) => item.currency.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [combinedData, searchTerm]);

    // Apply sorting and pagination locally
    const sortedAndPaginatedData = useMemo(() => {
        // Sort the data
        const sorted = [...filteredData].sort((a, b) => {
            // Special handling for custom columns
            if (orderBy === 'asset') {
                const aValue = a.currency;
                const bValue = b.currency;
                return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }
            
            if (orderBy === 'amount') {
                const aValue = a.remaining_amount;
                const bValue = b.remaining_amount;
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Handle other columns from the combined data
            const aValue = a[orderBy as keyof InsightData];
            const bValue = b[orderBy as keyof InsightData];

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            return 0;
        });

        // Apply pagination
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return sorted.slice(start, end);
    }, [filteredData, orderBy, order, page, rowsPerPage]);

    // Helper function to determine text color based on value - will be used when we have more data
    // const getValueColor = (value: number) => {
    //     if (value > 0) return 'success.main';
    //     if (value < 0) return 'error.main';
    //     return 'text.primary';
    // };

    if (!isLoading && !isLoadingUserPlans && !hasPortfolioInsightsAddon) {
        return (
            // Show upgrade message if user doesn't have the addon
            <Box sx={{
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                py: 6 
            }}>
                <LockIcon sx={{
                    fontSize: 48, 
                    color: 'text.secondary',
                    mb: 2 
                }} />
                <Typography variant="h6" sx={{mb: 1}}>
                    Short/Long Positions requires Portfolio Insights Addon
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{
                    mb: 3, 
                    textAlign: 'center' 
                }}>
                    Upgrade your plan to access detailed portfolio insights, including short/long positions, performance metrics, and more.
                </Typography>
                <Button variant="contained" color="primary" href="/plans">
                    Upgrade Now
                </Button>
            </Box>
        );
    }

    return (
        <PageLayout title={t('shortlong.title')}>
            <Box sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                p: 0
            }}>
                <Paper sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}>
                    <Box sx={{ 
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: theme.palette.divider
                    }}>
                        <TextField
                            fullWidth
                            placeholder={t('shortlong.search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead>
                                <TableRow>
                                    {headCells.map((headCell, index) => (
                                        <TableCell
                                            key={headCell.id}
                                            align={headCell.numeric ? 'right' : 'left'}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                            sx={index === 2 ? { borderRight: '1px solid rgba(81, 81, 81, 0.1)' } : {}}
                                        >
                                            <FilledTableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(headCell.id)}
                                            >
                                                {headCell.label}
                                                {orderBy === headCell.id ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {order === 'desc' ? t('common.table.sort.descending') : t('common.table.sort.ascending')}
                                                    </Box>
                                                ) : null}
                                            </FilledTableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(isLoading || isLoadingUserPlans) ? (
                                    // Loading skeleton
                                    Array.from(new Array(5)).map((_, index) => (
                                        <TableRow key={index}>
                                            {Array.from(new Array(headCells.length)).map((_, cellIndex) => (
                                                <TableCell key={cellIndex}>
                                                    <Skeleton variant="rectangular" width="100%" height={40} sx={{ my: 1 }} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : filteredData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={headCells.length} align="center">
                                            {t('shortlong.noData')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedAndPaginatedData.map((item) => (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '& td:nth-of-type(3)': { borderRight: '1px solid rgba(81, 81, 81, 0.1)' }
                                            }}
                                        >
                                            {/* Asset column - from gains data */}
                                            <TableCell>
                                                <Box sx={{ 
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <Avatar
                                                        src={`https://cointracking.info/img/coins/${item.coin_image}`}
                                                        sx={{ 
                                                            width: 24,
                                                            height: 24
                                                        }}
                                                    />
                                                    {getDisplayCoin(item.symbol, item.currency)}
                                                </Box>
                                            </TableCell>
                                            
                                            {/* Live Amount column */}
                                            <TableCell align="right">
                                                {isLivePriceLoading ? (
                                                    <Skeleton variant="rectangular" width="100%" height={40} sx={{ my: 1 }} />
                                                ) : item.live_amount ? (
                                                    <Box component="div" sx={{ 
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500
                                                    }}>
                                                        {formatCrypto(item.live_amount)}
                                                    </Box>
                                                ) : null}
                                            </TableCell>
                                            
                                            {/* Amount column - from gains data */}
                                            <TableCell align="right" sx={{ borderRight: '1px solid rgba(81, 81, 81, 0.1)' }}>
                                                {formatCrypto(item.remaining_amount)}
                                            </TableCell>
                                            
                                            {/* Columns populated with short-long data */}
                                            {Array.from({ length: 7 }).map((_, index) => (
                                                <TableCell key={`column-${index}`} align="right">
                                                    {item.shortLongData?.columns?.[index] ? 
                                                        formatCrypto(item.shortLongData.columns[index].amount) : '0'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <StyledPagination
                        rowsPerPageOptions={[5, 10, 25]}
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={(event) => handleChangeRowsPerPage(event as React.ChangeEvent<HTMLInputElement>)}
                    />
                </Paper>
            </Box>
        </PageLayout>
    );
};

export default ShortLongPage;
